import styled from 'styled-components';
import { Card } from '@teals/ui-web';

export const Page = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

export const Subtitle = styled.p`
  color: var(--text-muted);
  margin: 0.25rem 0 0;
  font-size: 0.9375rem;
`;

export const PanelCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

export const InsightStrip = styled.div<{ $variant?: 'default' | 'free' }>`
  padding: 0.875rem 1rem;
  border-radius: 10px;
  background: ${(p) =>
    p.$variant === 'free'
      ? 'var(--accent-subtle, #ecfdf5)'
      : 'var(--bg-surface, #f8fafc)'};
  border: 1px solid
    ${(p) =>
      p.$variant === 'free'
        ? 'var(--accent, #0f766e)'
        : 'var(--border, #e2e8f0)'};
  font-size: 0.875rem;
  color: var(--text-primary);
`;

export const FreePeriodBanner = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 0.75rem 1rem;
  padding: 1rem 1.1rem;
  border-radius: 10px;
  background: var(--accent-subtle, #ecfdf5);
  border: 1px solid var(--accent, #0f766e);
`;

export const FreePeriodTitle = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.25rem;
`;

export const FreePeriodCopy = styled.p`
  margin: 0;
  font-size: 0.875rem;
  color: var(--text-secondary, #64748b);
  line-height: 1.45;
`;

export const FeeCompare = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  margin-top: 0.5rem;
  font-size: 0.9375rem;
`;

export const FeeWas = styled.span`
  color: var(--text-muted);
  text-decoration: line-through;
  font-variant-numeric: tabular-nums;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
`;

export const FeeNow = styled.span`
  color: var(--accent, #0f766e);
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
`;

export const PlanLine = styled.p`
  margin: 0;
  font-size: 0.9375rem;
  color: var(--text-secondary, #64748b);
`;

export const BillHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

export const ProgressTrack = styled.div`
  height: 8px;
  border-radius: 999px;
  background: var(--border, #e2e8f0);
  overflow: hidden;
`;

export const ProgressFill = styled.div<{ $pct: number; $danger?: boolean }>`
  height: 100%;
  width: ${(p) => Math.min(100, Math.max(0, p.$pct))}%;
  background: ${(p) =>
    p.$danger ? 'var(--danger, #dc2626)' : 'var(--accent, #0f766e)'};
  border-radius: 999px;
  transition: width 0.2s ease;
`;

export const BillRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

export const BillRow = styled.div<{ $total?: boolean; $emphasis?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 0.75rem;
  font-size: ${(p) => (p.$total ? '1rem' : '0.875rem')};
  font-weight: ${(p) => (p.$total || p.$emphasis ? 600 : 400)};
  padding-top: ${(p) => (p.$total ? '0.5rem' : '0')};
  border-top: ${(p) => (p.$total ? '1px solid var(--border, #e2e8f0)' : 'none')};
  color: ${(p) =>
    p.$emphasis ? 'var(--accent, #0f766e)' : 'var(--text-primary)'};
`;

export const Mono = styled.span`
  font-variant-numeric: tabular-nums;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
`;

export const Footnote = styled.p`
  margin: 0;
  font-size: 0.75rem;
  color: var(--text-muted);
`;

export const DimensionRow = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

export const DimChip = styled.button<{ $active?: boolean }>`
  border: 1px solid
    ${(p) => (p.$active ? 'var(--accent, #0f766e)' : 'var(--border, #e2e8f0)')};
  background: ${(p) =>
    p.$active ? 'var(--accent-subtle, #ecfdf5)' : 'transparent'};
  color: var(--text-primary);
  border-radius: 999px;
  padding: 0.35rem 0.75rem;
  font-size: 0.8125rem;
  cursor: pointer;
`;

export const KpiStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

export const KpiCard = styled.div`
  padding: 0.875rem 1rem;
  border: 1px solid var(--border, #e2e8f0);
  border-radius: 10px;
  background: var(--bg-elevated, #fff);
`;

export const KpiLabel = styled.div`
  font-size: 0.75rem;
  color: var(--text-muted);
  margin-bottom: 0.25rem;
`;

export const KpiValue = styled.div`
  font-size: 1.25rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
`;

export const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

export const AppBudgetRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  padding: 0.75rem 0;
  border-bottom: 1px solid var(--border, #e2e8f0);
`;

export const SectionTitle = styled.h3`
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
`;
